<script>
import DatePicker from 'primevue/datepicker';

export default {
    name: 'PartnerFunnel',

    components: { DatePicker },

    props: {
        partnerId: {
            type: Number,
            required: true,
        },
        linkId: {
            type: Number,
            required: true,
        },
    },

    data() {
        return {
            dateRange: null,
            conversionMode: 'traffic',
        };
    },

    computed: {
        funnel() {
            return this.$store.getters.FUNNEL_DATA;
        },
        isLoading() {
            return this.$store.getters.IS_FUNNEL_LOADING;
        },
        trafficCount() {
            if (!this.funnel) return 0;
            const traffic = this.funnel.steps.find((s) => s.name === 'traffic');
            return traffic ? traffic.count : 0;
        },
        displayedSteps() {
            if (!this.funnel) return [];
            return this.funnel.steps.map((step, i) => {
                let display_pct = null;
                if (i > 0) {
                    const base =
                        this.conversionMode === 'traffic'
                            ? this.trafficCount
                            : this.funnel.steps[i - 1].count;
                    display_pct = base > 0 ? Math.round((step.count / base) * 100) : 0;
                }
                return { ...step, display_pct };
            });
        },
        worstDropIndex() {
            if (!this.funnel) return -1;
            let maxLost = 0;
            let idx = -1;
            this.funnel.steps.forEach((s, i) => {
                if (i === 0) return;
                const lost = this.funnel.steps[i - 1].count - s.count;
                if (lost > maxLost) {
                    maxLost = lost;
                    idx = i;
                }
            });
            return idx;
        },
    },

    mounted() {
        this.fetchFunnel();
    },

    methods: {
        async fetchFunnel() {
            const params = {};
            if (this.dateRange && this.dateRange[0]) {
                params.date_from = this.dateRange[0].toISOString().slice(0, 10);
                // Support same-day range: if end date is not set, use start date as end
                params.date_to = (this.dateRange[1] || this.dateRange[0]).toISOString().slice(0, 10);
            }
            await this.$store.dispatch('loadPartnerFunnel', {
                partnerId: this.partnerId,
                linkId: this.linkId,
                params,
            });
        },

        onDateRangeChange(val) {
            // Fire on complete range or same-day selection (val[1] is null when same date clicked twice)
            if (val && val[0]) {
                this.fetchFunnel();
            }
        },

        resetDateRange() {
            this.dateRange = null;
            this.fetchFunnel();
        },

        barWidth(count) {
            if (!this.trafficCount) return 0;
            return Math.round((count / this.trafficCount) * 100);
        },

        barHealthClass(index) {
            if (!this.funnel) return 'bar-neutral';
            if (index === 0) return 'bar-neutral';
            const prev = this.funnel.steps[index - 1];
            const curr = this.funnel.steps[index];
            if (!prev.count) return 'bar-neutral';
            const pct = (curr.count / prev.count) * 100;
            if (pct >= 80) return 'bar-green';
            if (pct >= 40) return 'bar-amber';
            return 'bar-red';
        },

        dropOff(index) {
            if (index === 0 || !this.funnel) return null;
            const prev = this.funnel.steps[index - 1];
            const curr = this.funnel.steps[index];
            const lost = prev.count - curr.count;
            const pct = prev.count > 0 ? Math.round((lost / prev.count) * 100) : 0;
            return { lost, pct };
        },

        conversionClass(pct) {
            if (pct === null || pct === undefined) return '';
            if (pct >= 80) return 'pct-green';
            if (pct >= 40) return 'pct-yellow';
            return 'pct-red';
        },

        stepLabel(name) {
            const labels = {
                traffic: 'Traffic',
                visit: 'Visit (unique)',
                signup: 'Signup',
                first_action: 'First action',
                core_action: 'Core action',
                retention_7d: 'Retention 7d',
                retention_30d: 'Retention 30d',
            };
            return labels[name] || name;
        },

        stepIcon(name) {
            const icons = {
                traffic: '🔗',
                visit: '👁',
                signup: '✏️',
                first_action: '⚡',
                core_action: '🎯',
                retention_7d: '📅',
                retention_30d: '🔄',
            };
            return icons[name] || '•';
        },

        stepHint(name) {
            const hints = {
                traffic: 'Clicks on the partner referral link (?ref=…). Counted once per click, including repeat visits from the same user.',
                visit: 'Unique visitors: deduplicated by IP per day. Reflects true reach — multiple clicks from the same person count as one.',
                signup: 'Users who completed registration after clicking the link. Attribution window: 30 days from first click.',
                first_action: 'Registered users who performed any action (search, download, save) at least once after signing up.',
                core_action: 'Users who downloaded at least one asset — the primary conversion goal.',
                retention_7d: 'Registered users who returned and were active at least once within 7 days of their signup date.',
                retention_30d: 'Registered users who returned and were active at least once within 30 days of their signup date.',
            };
            return hints[name] || '';
        },
    },
};
</script>

<template>
    <div class="partner-funnel">
        <h4>Conversion Funnel</h4>

        <div class="funnel-controls">
            <DatePicker
                v-model="dateRange"
                selection-mode="range"
                date-format="dd.mm.yy"
                :manual-input="false"
                placeholder="Select date range"
                class="funnel-datepicker"
                show-button-bar
                @update:model-value="onDateRangeChange"
            />
            <button :class="['toggle-btn', !dateRange ? 'toggle-btn--active' : '']" @click="resetDateRange">All time</button>
        </div>

        <div class="funnel-mode-toggle">
            <button
                :class="['toggle-btn', conversionMode === 'traffic' ? 'toggle-btn--active' : '']"
                @click="conversionMode = 'traffic'"
            >
                vs Traffic
            </button>
            <button
                :class="['toggle-btn', conversionMode === 'previous' ? 'toggle-btn--active' : '']"
                @click="conversionMode = 'previous'"
            >
                vs Previous step
            </button>
        </div>

        <div v-if="isLoading" class="loading-text">Loading funnel…</div>

        <div v-else-if="!funnel" class="empty-text">No data available.</div>

        <table v-else class="funnel-table">
            <thead>
                <tr>
                    <th>Step</th>
                    <th>Count</th>
                    <th>Drop</th>
                    <th>Conversion</th>
                    <th>Bar</th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="(step, index) in displayedSteps"
                    :key="step.name"
                    :class="{ 'row-worst': index === worstDropIndex }"
                >
                    <td class="step-cell">
                        <span class="step-icon">{{ stepIcon(step.name) }}</span>
                        <span class="step-hint-wrap">
                            {{ stepLabel(step.name) }}
                            <span class="step-hint-bubble">{{ stepHint(step.name) }}</span>
                        </span>
                    </td>
                    <td class="count-cell">{{ step.count.toLocaleString() }}</td>
                    <td class="drop-cell">
                        <template v-if="dropOff(index)">
                            <span v-if="dropOff(index).lost > 0" class="drop-negative">
                                ▼ {{ dropOff(index).lost.toLocaleString() }}
                                ({{ dropOff(index).pct }}%)
                            </span>
                            <span v-else class="drop-neutral">±0</span>
                        </template>
                        <span v-else class="drop-neutral">—</span>
                    </td>
                    <td>
                        <span
                            v-if="step.display_pct !== null && step.display_pct !== undefined"
                            :class="['pct-badge', conversionClass(step.display_pct)]"
                        >
                            {{ step.display_pct }}%
                        </span>
                        <span v-else class="pct-badge pct-none">—</span>
                    </td>
                    <td class="bar-cell">
                        <div class="funnel_bar">
                            <div
                                :class="['funnel_bar_fill', barHealthClass(index)]"
                                :style="{ width: `${barWidth(step.count)}%` }"
                            />
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</template>

<style scoped>
.partner-funnel {
    margin-top: 16px;
    padding: 12px;
    background: #f0f9ff;
    border-left: 3px solid #7dd3fc;
    border-radius: 4px;
}
.partner-funnel h4 {
    margin: 0 0 10px;
    font-size: 14px;
    color: #0369a1;
}
.funnel-controls {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
    flex-wrap: wrap;
}
.funnel-datepicker {
    font-size: 12px;
}
:deep(.funnel-datepicker .p-datepicker-input) {
    padding: 4px 8px;
    font-size: 12px;
    height: 28px;
}
.funnel-mode-toggle {
    display: flex;
    gap: 4px;
    margin-bottom: 10px;
}
.toggle-btn {
    padding: 0 10px;
    font-size: 12px;
    height: 28px;
    border: 1px solid #cbd5e1;
    border-radius: 4px;
    background: #fff;
    color: #475569;
    cursor: pointer;
    transition: all 0.15s;
}
.toggle-btn:hover {
    border-color: #94a3b8;
    background: #f8fafc;
}
.toggle-btn--active {
    background: #0369a1;
    border-color: #0369a1;
    color: #fff;
}
.funnel-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;
}
.funnel-table th,
.funnel-table td {
    padding: 6px 8px;
    border-bottom: 1px solid #e5e7eb;
    text-align: left;
}
.funnel-table tr.row-worst {
    background: rgba(220, 38, 38, 0.04);
    border-left: 3px solid #ef4444;
}
.step-cell {
    white-space: nowrap;
}
.step-icon {
    margin-right: 5px;
    font-size: 13px;
}
.count-cell {
    font-weight: 600;
    font-variant-numeric: tabular-nums;
}
.drop-cell {
    font-size: 12px;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
}
.drop-negative {
    color: #991b1b;
    font-weight: 600;
}
.drop-neutral {
    color: #9ca3af;
}
.pct-badge {
    font-size: 12px;
    padding: 2px 6px;
    border-radius: 10px;
    font-weight: 600;
}
.pct-green  { background: #dcfce7; color: #166534; }
.pct-yellow { background: #fef9c3; color: #854d0e; }
.pct-red    { background: #fee2e2; color: #991b1b; }
.pct-none   { background: #f3f4f6; color: #6b7280; }
.bar-cell {
    width: 180px;
}
.funnel_bar {
    height: 8px;
    background: #e5e7eb;
    border-radius: 4px;
    overflow: hidden;
}
.funnel_bar_fill {
    height: 100%;
    border-radius: 4px;
    transition: width 0.3s ease;
}
.bar-neutral { background: #38bdf8; }
.bar-green   { background: #22c55e; }
.bar-amber   { background: #f59e0b; }
.bar-red     { background: #ef4444; }

/* ---- Step tooltips ---- */
.step-hint-wrap {
    position: relative;
    cursor: default;
    border-bottom: 1px dotted #94a3b8;
    display: inline;
}
.step-hint-bubble {
    position: absolute;
    bottom: calc(100% + 6px);
    left: 0;
    z-index: 100;
    width: 240px;
    background: #1e293b;
    color: #f1f5f9;
    font-size: 11px;
    line-height: 1.5;
    padding: 6px 8px;
    border-radius: 6px;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.15s ease;
    white-space: normal;
    font-weight: 400;
}
.step-hint-bubble::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 12px;
    border: 5px solid transparent;
    border-top-color: #1e293b;
}
.step-hint-wrap:hover .step-hint-bubble {
    opacity: 1;
}
</style>
